import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCiudades } from '../actions/ciudadActions';
import QRCode from 'qrcode';
import placeholder from '../assets/user-profile-photo.png';
import { getUploadUrl } from '../util/utilFunctions';
import socioService from '../services/socioService';
import PrintPreviewModal from './PrintPreviewModal';
import './SocioFicha.css';

const SocioFicha = ({ showModalFicha, closeModalFicha, sociof, fotoUrlFicha }) => {
  const dispatch = useDispatch();
  const ciudades = useSelector((state) => state.ciudad.ciudades);
  const fotoCandidates = useMemo(() => {
    return [getUploadUrl(sociof?.foto), fotoUrlFicha, placeholder].filter((url, index, list) => url && list.indexOf(url) === index);
  }, [fotoUrlFicha, sociof?.foto]);
  const [fotoIndex, setFotoIndex] = useState(0);
  const vistaPreviaFoto = fotoCandidates[fotoIndex] || placeholder;
  const [qrUrl, setQrUrl] = useState('');
  const [verificationUrl, setVerificationUrl] = useState('');
  const [showPrintPreview, setShowPrintPreview] = useState(false);

  const nombreCompleto = useMemo(() => {
    return [sociof?.nombres, sociof?.apPat, sociof?.apMat].filter(Boolean).join(' ');
  }, [sociof]);

  useEffect(() => {
    dispatch(fetchCiudades());
  }, [dispatch]);

  useEffect(() => {
    setFotoIndex(0);
  }, [fotoCandidates]);

  const handleFotoError = () => {
    setFotoIndex((currentIndex) => (
      currentIndex < fotoCandidates.length - 1 ? currentIndex + 1 : currentIndex
    ));
  };

  useEffect(() => {
    let active = true;
    if (!showModalFicha || !sociof?.id) {
      setVerificationUrl('');
      setQrUrl('');
      return undefined;
    }

    socioService.getSocioVerificationToken(sociof.id)
      .then((response) => {
        if (!active) return;
        const token = response?.token;
        const url = token ? window.location.origin + '/ver-socio/' + encodeURIComponent(token) : '';
        setVerificationUrl(url);
      })
      .catch(() => {
        if (!active) return;
        setVerificationUrl('');
        setQrUrl('');
      });

    return () => { active = false; };
  }, [showModalFicha, sociof?.id]);

  useEffect(() => {
    let active = true;
    if (!showModalFicha || !verificationUrl) {
      setQrUrl('');
      return undefined;
    }

    QRCode.toDataURL(verificationUrl, {
      errorCorrectionLevel: 'M',
      margin: 1,
      width: 180,
      color: { dark: '#12301f', light: '#ffffff' },
    }).then((url) => {
      if (active) setQrUrl(url);
    }).catch(() => {
      if (active) setQrUrl('');
    });

    return () => { active = false; };
  }, [showModalFicha, verificationUrl]);

  const obtenerNombreCiudad = (codciu) => {
    const ciudad = ciudades.find((item) => item.id === codciu);
    return ciudad ? ciudad.nombre : 'N/A';
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const parsed = new Date(date);
    return Number.isNaN(parsed.getTime()) ? 'N/A' : parsed.toLocaleDateString('es-BO');
  };

  const estadoNormalizado = (sociof?.estado ?? '').toString().trim().toLowerCase();
  const estadoActivo = sociof?.estado === true
    || sociof?.estado === 1
    || estadoNormalizado === '1'
    || estadoNormalizado === 'activo'
    || estadoNormalizado === 'activa'
    || estadoNormalizado === 'habilitado'
    || estadoNormalizado === 'habilitada';
  const estadoBaja = sociof?.estado === 2 || estadoNormalizado === '2' || estadoNormalizado.includes('baja');
  const estadoTexto = estadoActivo ? 'Activo' : estadoBaja ? 'Dado de baja' : 'Inactivo';
  const gradoBase = [sociof?.tit, sociof?.grado, sociof?.titulo, sociof?.nrotit].filter(Boolean).join(' ').toLowerCase();
  const gradoTexto = gradoBase.includes('tec') || gradoBase.includes('técnico') || gradoBase.includes('tecnico') || gradoBase.includes('superior') ? 'Técnico Superior' : 'Ingeniero';
  const universidad = sociof?.suniv || sociof?.universidad || 'No registrada';
  const nroTitulo = sociof?.nrotit || 'No registrado';
  const statusClass = estadoActivo ? 'is-active' : estadoBaja ? 'is-retired' : 'is-inactive';

  return (
    <>
      <Modal
        show={showModalFicha}
        onHide={() => closeModalFicha(false)}
        backdrop="static"
        keyboard={false}
        size="lg"
        centered
        className="socio-carnet-modal"
      >
        <Modal.Header closeButton className="socio-carnet-modal-header">
          <Modal.Title>Carnet del Asociado</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="socio-carnet-print-area">
            <section className="socio-carnet-card" aria-label="Carnet del asociado">
              <div className="socio-carnet-circuit" />
              <header className="socio-carnet-header">
                <img src="/assets/ciab_logo.jpg" alt="CIAB" className="socio-carnet-logo" />
                <div>
                  <div className="socio-carnet-kicker">Colegio de Ingenieros Agronomos de Bolivia</div>
                  <h2>Filial Tarija</h2>
                  <p>Carnet de asociado</p>
                </div>
                <span className={'socio-carnet-status ' + statusClass}>{estadoTexto}</span>
              </header>

              <div className="socio-carnet-main">
                <div className="socio-carnet-photo-wrap">
                  <img
                  src={vistaPreviaFoto || placeholder}
                  alt="Foto del asociado"
                  className="socio-carnet-photo"
                  onError={handleFotoError}
                />
                </div>
                <div className="socio-carnet-identity">
                  <div className="socio-carnet-label">Nombre completo</div>
                  <h3>{nombreCompleto || 'Sin nombre registrado'}</h3>
                  <div className="socio-carnet-role-row">
                    <span className="socio-carnet-role">{gradoTexto}</span>
                    <span className="socio-carnet-chip">CIAB-TJA</span>
                  </div>
                  <div className="socio-carnet-grid">
                    <div><span>Registro</span><strong>{sociof?.codreg || 'N/A'}</strong></div>
                    <div><span>CI</span><strong>{sociof?.ci || 'N/A'}</strong></div>
                    <div><span>Fecha registro</span><strong>{formatDate(sociof?.fereg)}</strong></div>
                    <div><span>Ciudad</span><strong>{obtenerNombreCiudad(sociof?.codciu)}</strong></div>
                  </div>
                </div>
              </div>
              <div className="socio-carnet-details">
                <div><span>Nro. titulo</span><strong>{nroTitulo}</strong></div>
                <div><span>Universidad asociada</span><strong>{universidad}</strong></div>
              </div>

              <footer className="socio-carnet-footer">
                <div className="socio-carnet-qrbox">
                  {qrUrl ? <img src={qrUrl} alt="QR de verificacion del asociado" /> : <span>QR</span>}
                </div>
              </footer>
            </section>
          </div>
        </Modal.Body>
        <Modal.Footer className="button-group socio-carnet-actions">
          <button className="button btn-cancel" type="button" onClick={() => closeModalFicha(false)}>
            <i className="bi bi-arrow-left"></i> Volver
          </button>
          <button className="button btn-accent" type="button" onClick={() => setShowPrintPreview(true)}>
            <i className="bi bi-printer"></i> Imprimir
          </button>
        </Modal.Footer>
      </Modal>

      <PrintPreviewModal
        show={showPrintPreview}
        handleClose={() => setShowPrintPreview(false)}
        socio={sociof}
        vistaPreviaFoto={vistaPreviaFoto}
        qrUrl={qrUrl}
        ciudad={obtenerNombreCiudad(sociof?.codciu)}
        estadoTexto={estadoTexto}
        gradoTexto={gradoTexto}
        universidad={universidad}
        nroTitulo={nroTitulo}
      />
    </>
  );
};

export default SocioFicha;
